import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  QrCode as QrCodeIcon, 
  Download, 
  Printer,
  Smartphone,
  Store,
  CreditCard,
  Loader2,
  Copy,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Wifi
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QRCode = () => {
  const { organization, hasSubscription } = useAuth();
  const navigate = useNavigate();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [noSubscription, setNoSubscription] = useState(false);

  useEffect(() => {
    fetchQRCode();
  }, []);

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/qrcode`);
      setQrData(response.data);
      setNoSubscription(false);
    } catch (error) {
      console.error("Error fetching QR code:", error);
      if (error.response?.status === 403) {
        setNoSubscription(true);
      } else {
        toast.error("Erreur lors du chargement du QR Code");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/qrcode/regenerate`);
      // Add timestamp to force re-render
      setQrData({ ...response.data, _refreshed: Date.now() });
      toast.success("QR Code régénéré avec succès !");
    } catch (error) {
      console.error("Error regenerating QR code:", error);
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownload = () => {
    if (!qrData) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrData.qr_code_base64}`;
    link.download = `qr-code-${organization?.name || 'responsia'}.png`;
    link.click();
    toast.success("QR Code téléchargé !");
  };

  const handleCopyLink = async () => {
    if (!qrData) return;
    
    try {
      await navigator.clipboard.writeText(qrData.review_page_url);
      toast.success("Lien copié dans le presse-papier !");
    } catch (error) {
      toast.error("Erreur lors de la copie");
    }
  };

  const handlePrint = () => {
    if (!qrData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${organization?.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: system-ui, sans-serif;
              margin: 0;
              padding: 20px;
            }
            img {
              max-width: 300px;
              margin-bottom: 20px;
            }
            h1 {
              font-size: 24px;
              color: #0f172a;
              margin-bottom: 8px;
            }
            p {
              color: #64748b;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <img src="data:image/png;base64,${qrData.qr_code_base64}" alt="QR Code" />
          <h1>${organization?.name}</h1>
          <p>Scannez pour laisser un avis</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadNFCSticker = async () => {
    if (!qrData) return;
    
    try {
      // Use html2canvas to capture the NFC sticker element
      const html2canvas = (await import('html2canvas')).default;
      const element = document.getElementById('nfc-sticker');
      
      if (!element) {
        toast.error("Erreur: élément non trouvé");
        return;
      }
      
      const canvas = await html2canvas(element, {
        scale: 3, // High resolution
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `autocollant-nfc-${organization?.name?.replace(/\s+/g, '-') || 'responsia'}.png`;
      link.click();
      
      toast.success("Autocollant NFC téléchargé !");
    } catch (error) {
      console.error("Error downloading NFC sticker:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handlePrintNFCSticker = () => {
    if (!qrData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Autocollant NFC - ${organization?.name}</title>
          <style>
            @page {
              size: 85mm 130mm;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: system-ui, -apple-system, sans-serif;
              width: 85mm;
              padding: 5mm;
              background: white;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 2mm;
              border-bottom: 0.5mm solid #333;
              margin-bottom: 3mm;
            }
            .header .url {
              font-size: 10pt;
              text-decoration: underline;
            }
            .header .nfc {
              display: flex;
              align-items: center;
              gap: 1mm;
              font-weight: bold;
              font-size: 10pt;
            }
            .title-section {
              text-align: center;
              margin: 3mm 0;
            }
            .subtitle {
              font-size: 8pt;
              letter-spacing: 2px;
              color: #666;
              margin-bottom: 1mm;
            }
            .establishment-name {
              font-size: 18pt;
              font-weight: 900;
              line-height: 1.1;
              margin-bottom: 1mm;
            }
            .tagline {
              font-size: 8pt;
              color: #666;
              font-style: italic;
            }
            .nfc-instruction {
              display: flex;
              align-items: center;
              gap: 3mm;
              margin: 4mm 0;
              padding: 0 2mm;
            }
            .nfc-icon {
              width: 12mm;
              height: 12mm;
            }
            .nfc-text {
              font-weight: bold;
              font-size: 10pt;
              line-height: 1.3;
            }
            .separator {
              display: flex;
              align-items: center;
              gap: 2mm;
              margin: 3mm 0;
            }
            .separator .line {
              flex: 1;
              border-top: 0.3mm solid #ccc;
            }
            .separator .text {
              font-size: 8pt;
              color: #999;
            }
            .qr-section {
              text-align: center;
            }
            .qr-code {
              width: 40mm;
              height: 40mm;
            }
            .scan-text {
              font-size: 9pt;
              font-weight: bold;
              letter-spacing: 1px;
              margin-top: 2mm;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <span class="url">www.responsia.fr</span>
            <div class="nfc">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M8.5 12.5c0-1.93 1.57-3.5 3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M6 12c0-3.31 2.69-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M3.5 12c0-4.69 3.81-8.5 8.5-8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
              </svg>
              NFC
            </div>
          </div>
          
          <div class="title-section">
            <p class="subtitle">VOTRE AVIS COMPTE POUR</p>
            <h1 class="establishment-name">${organization?.name || "Nom de l'établissement"}</h1>
            <p class="tagline">Laissez votre retour en 10 secondes</p>
          </div>
          
          <div class="nfc-instruction">
            <svg class="nfc-icon" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="4" fill="currentColor"/>
              <path d="M24 14c5.52 0 10 4.48 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              <path d="M24 8c8.84 0 16 7.16 16 16" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              <path d="M24 2c12.15 0 22 9.85 22 22" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <div class="nfc-text">
              Posez votre téléphone<br/>pour laisser votre avis
            </div>
          </div>
          
          <div class="separator">
            <div class="line"></div>
            <span class="text">ou</span>
            <div class="line"></div>
          </div>
          
          <div class="qr-section">
            <img class="qr-code" src="data:image/png;base64,${qrData.qr_code_base64}" alt="QR Code" />
            <p class="scan-text">SCANNER LE QR CODE</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const tips = [
    {
      icon: Store,
      title: "Comptoir",
      description: "Placez le QR code près de la caisse pour capturer les clients satisfaits"
    },
    {
      icon: CreditCard,
      title: "Carte de visite",
      description: "Intégrez le QR code sur vos cartes de visite ou flyers"
    },
    {
      icon: Smartphone,
      title: "SMS / Email",
      description: "Partagez le lien direct par SMS ou email après une prestation"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  // No subscription - show message to subscribe
  if (noSubscription) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="qrcode-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Votre QR Code
          </h1>
          <p className="text-slate-500 mt-1">
            Partagez ce QR code pour collecter des avis clients
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center max-w-lg mx-auto">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-3">
            Abonnement requis
          </h2>
          <p className="text-slate-500 mb-6">
            Pour accéder à votre QR Code et collecter des avis clients, vous devez avoir un abonnement actif.
          </p>
          <Button
            onClick={() => navigate("/subscription")}
            className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
            data-testid="subscribe-button"
          >
            Voir les offres d'abonnement
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="qrcode-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
          Votre QR Code
        </h1>
        <p className="text-slate-500 mt-1">
          Partagez ce QR code pour collecter des avis clients
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* QR Code Display */}
        <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center">
          <div className="inline-block bg-white p-6 rounded-2xl shadow-lg shadow-slate-200/50 mb-6">
            {qrData?.qr_code_base64 ? (
              <img 
                key={qrData._refreshed || qrData.id || qrData.created_at}
                src={`data:image/png;base64,${qrData.qr_code_base64}`}
                alt="QR Code"
                className="w-48 h-48 md:w-64 md:h-64"
                data-testid="qr-code-image"
              />
            ) : (
              <div className="w-48 h-48 md:w-64 md:h-64 bg-slate-100 rounded-xl flex items-center justify-center">
                <QrCodeIcon className="w-16 h-16 text-slate-300" />
              </div>
            )}
          </div>

          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {organization?.name}
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            Scannez pour laisser un avis
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={handleDownload}
              className="rounded-full bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
              data-testid="download-qrcode-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="rounded-full w-full sm:w-auto"
              data-testid="print-qrcode-button"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
          </div>
        </div>

        {/* Link & Tips */}
        <div className="space-y-6">
          {/* Direct Link */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4">Lien direct</h3>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
              <input
                type="text"
                value={qrData?.review_page_url || ""}
                readOnly
                className="flex-1 bg-transparent text-sm text-slate-600 truncate outline-none"
                data-testid="review-page-url"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="flex-shrink-0"
                data-testid="copy-link-button"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <a 
                href={qrData?.review_page_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <Button variant="ghost" size="sm" data-testid="open-link-button">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-6 border border-violet-100">
            <h3 className="font-semibold text-slate-900 mb-4">
              Conseils d'utilisation
            </h3>
            <div className="space-y-4">
              {tips.map((tip, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <tip.icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{tip.title}</p>
                    <p className="text-sm text-slate-600">{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
            <h4 className="font-medium text-amber-900 mb-2">Comment ça marche ?</h4>
            <ol className="text-sm text-amber-800 space-y-2">
              <li>1. Le client scanne le QR code</li>
              <li>2. Il note son expérience de 1 à 5 étoiles</li>
              <li>3. Si 4+ étoiles → Redirection vers Google Avis</li>
              <li>4. Si moins → L'avis reste privé pour amélioration</li>
            </ol>
          </div>
        </div>
      </div>

      {/* NFC Sticker Section */}
      <div className="mt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
            <Wifi className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Autocollant NFC</h2>
            <p className="text-slate-500 text-sm">Pour vos cartes NFC et supports adhésifs</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* NFC Sticker Preview */}
            <div 
              id="nfc-sticker"
              className="bg-white border-2 border-slate-200 rounded-xl p-6 mx-auto"
              style={{ width: '320px', fontFamily: 'system-ui, sans-serif' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-300">
                <span className="text-sm font-medium text-slate-800 underline">www.responsia.fr</span>
                <div className="flex items-center gap-1">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                    <path d="M8.5 12.5c0-1.93 1.57-3.5 3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M6 12c0-3.31 2.69-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M3.5 12c0-4.69 3.81-8.5 8.5-8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  </svg>
                  <span className="text-sm font-bold">NFC</span>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mt-4">
                <p className="text-xs tracking-widest text-slate-600 mb-1">VOTRE AVIS COMPTE POUR</p>
                <h3 className="text-2xl font-black text-slate-900 leading-tight mb-1">
                  {organization?.name || "Nom de l'établissement"}
                </h3>
                <p className="text-xs text-slate-500 italic">Laissez votre retour en 10 secondes</p>
              </div>

              {/* NFC Instructions */}
              <div className="flex items-center gap-3 mt-4 mb-4 px-2">
                <svg className="w-12 h-12 flex-shrink-0" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="4" fill="currentColor"/>
                  <path d="M24 14c5.52 0 10 4.48 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none"/>
                  <path d="M24 8c8.84 0 16 7.16 16 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none"/>
                  <path d="M24 2c12.15 0 22 9.85 22 22" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none"/>
                </svg>
                <div>
                  <p className="font-bold text-slate-900 text-sm leading-tight">Posez votre téléphone</p>
                  <p className="font-bold text-slate-900 text-sm leading-tight">pour laisser votre avis</p>
                </div>
              </div>

              {/* Separator */}
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 border-t border-slate-300"></div>
                <span className="text-xs text-slate-500">ou</span>
                <div className="flex-1 border-t border-slate-300"></div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                {qrData?.qr_code_base64 ? (
                  <img 
                    src={`data:image/png;base64,${qrData.qr_code_base64}`}
                    alt="QR Code"
                    className="w-40 h-40"
                  />
                ) : (
                  <div className="w-40 h-40 bg-slate-100 rounded flex items-center justify-center">
                    <QrCodeIcon className="w-12 h-12 text-slate-300" />
                  </div>
                )}
              </div>

              {/* Footer */}
              <p className="text-center text-xs font-bold tracking-wider text-slate-700 mt-2">
                SCANNER LE QR CODE
              </p>
            </div>

            {/* Instructions & Download */}
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-violet-600" />
                  Qu'est-ce que le NFC ?
                </h4>
                <p className="text-sm text-slate-600 mb-3">
                  Le NFC (Near Field Communication) permet à vos clients de laisser un avis simplement en approchant leur smartphone d'une carte ou d'un autocollant.
                </p>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600">•</span>
                    Imprimez cet autocollant et collez-le sur votre carte NFC
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600">•</span>
                    Programmez votre carte NFC avec le lien de votre page d'avis
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600">•</span>
                    Les clients peuvent utiliser le NFC ou scanner le QR code
                  </li>
                </ul>
              </div>

              <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                <h4 className="font-semibold text-violet-900 mb-2">Lien à programmer sur la carte NFC</h4>
                <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-violet-200">
                  <input
                    type="text"
                    value={qrData?.review_page_url || ""}
                    readOnly
                    className="flex-1 bg-transparent text-xs text-slate-600 truncate outline-none"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-shrink-0 h-7 px-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleDownloadNFCSticker}
                  className="rounded-full bg-slate-900 hover:bg-slate-800 flex-1"
                  data-testid="download-nfc-sticker"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger l'autocollant
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePrintNFCSticker}
                  className="rounded-full flex-1"
                  data-testid="print-nfc-sticker"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCode;
